import { NetworkStatus } from '@apollo/client'
import { CurrentUserFirmsProps } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    GetSortInput,
    ProjectItem,
} from '@deltek/specification-client/core/Models/projectItem'
import { TableFetchNextDataProps } from '@deltek/specification-client/core/Widgets/SpecTable'
import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Skeleton from '@material-ui/lab/Skeleton'
import {
    GroupProjectItem,
    ProjectGroupCountItem,
    TableProjectItem,
} from 'Models/project'
import clsx from 'clsx'
import React, { ChangeEvent } from 'react'
import { ProjectFilterContext } from '../ProjectFilterContext'
import ProjectGridItem from '../ProjectGridItem'
import RecentProjectsLoadState from '../RecentProjectsLoadState'
import ProjectGridFilter from './ProjectGridFilter'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        '& .MuiAccordion-root.Mui-expanded': {
            margin: theme.spacing(0),
        },
        margin: theme.spacing(0),
        marginBottom: theme.spacing(1),
    },
    gridViewInner: (props: ProjectGridViewStypeProps) => ({
        minHeight: props.projectCount && props.projectCount > 25 ? 700 : 'auto',
        marginBottom: theme.spacing(2),
    }),
    heading: {
        ...theme.typography.body2,
        flexBasis: '33.33%',
        flexShrink: 0,
        color: '#0069AA',
        textTransform: 'uppercase',
        paddingTop: theme.spacing(0.5),
    },
    loadStateContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gridGap: 4,
        marginTop: theme.spacing(0.5),
    },
    itemContainer: {
        minHeigh: 135,
        position: 'relative',
        backgroundColor: theme.palette.common.white,
        '& > :nth-child(1), :nth-child(2)': {
            marginBottom: theme.spacing(0.3),
        },
    },
    accordionSumm: {
        borderBottom: '1px solid #c9c9c9',
        padding: 0,
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(0.125, 0.125, 0.125, -1),
        },
        '& .MuiAccordionSummary-content': {
            marginLeft: theme.spacing(-1),
        },
    },
    accordion: {
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
        boxShadow: 'none',
        '& .MuiAccordionSummary-root.Mui-expanded': {
            minHeight: theme.spacing(6),
        },
        backgroundColor: '#f2f2f2',
    },
    accordionDetail: {
        padding: theme.spacing(0),
        flexWrap: 'wrap',
        display: 'block',
        marginTop: theme.spacing(0.5),
    },
    expansionIcon: {
        fill: '#0069AA',
    },
    loadMoreContainer: {
        height: 40,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        bottom: 0,
        paddingTop: 27,
        paddingBottom: 29,
    },
    loadMoreButton: {
        width: 200,
        borderRadius: 100,
        background: theme.palette.primary.main,
        textTransform: 'capitalize',
        alignSelf: 'center',
        position: 'absolute',
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: 'rgb(0, 106, 149)',
        },
    },
    grayText: {
        ...theme.typography.h1,
        color: theme.palette.grey[700],
    },
    centerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 130,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gridGap: 4,
    },
    projectGridViewWrapper: (props: ProjectGridViewStypeProps) => ({
        overflowY: 'auto',
        height: 600,
    }),
}))
interface ProjectGridViewStypeProps {
    projectCount?: number
}
interface ProjectGridViewProps extends ProjectGridViewStypeProps {
    isFilterExpanded: boolean
    handleExpandCollapse: (
        val: string | undefined
    ) => (event: ChangeEvent<{}>, expansionVal: boolean) => void
    isGridExpanded: Record<string, boolean>
    projectData: Partial<ProjectItem>[] | undefined
    handleFavoriteChange?: (favorite: boolean, projectId: string) => void
    projectDataStatus?: number
    fetchNextData?: (
        props: Partial<TableFetchNextDataProps<TableProjectItem>>
    ) => void
    defaultPageSize: number
    skipCount?: number
    setSkipCount?: (val: number) => void
    localStorageKey: string
    localStorageItemKey: (val: string) => string
    projectSortValues?: Partial<GetSortInput>[]
    projectGroupCounts?: Partial<ProjectGroupCountItem>[]
    projectsLoading: boolean
    openRemoveProjectConfirmation: (projectId: string | undefined) => void
    isAdmin?: boolean
    activeFirmInfo: CurrentUserFirmsProps
}
const ProjectGridView: React.FC<ProjectGridViewProps> = ({
    isFilterExpanded,
    handleExpandCollapse,
    isGridExpanded,
    projectData,
    handleFavoriteChange,
    projectDataStatus,
    fetchNextData,
    projectCount,
    skipCount,
    setSkipCount,
    defaultPageSize,
    localStorageKey,
    localStorageItemKey,
    projectSortValues,
    projectGroupCounts,
    projectsLoading,
    openRemoveProjectConfirmation,
    isAdmin,
    activeFirmInfo,
}) => {
    const classes = useStyles({ projectCount })
    const [allProject, setAllProject] = React.useState<
        Partial<GroupProjectItem>[]
    >([])
    const [isProjectNumberVisible, setIsProjectNumberVisible] = React.useState<
        boolean
    >(false)
    const { projectFilterValues } = React.useContext(ProjectFilterContext)
    const [isLoading, setIsLoading] = React.useState<boolean>(false)

    React.useEffect(() => {
        if (projectData)
            setAllProject(
                getProjectStructuredData(projectData, projectGroupCounts)
            )
    }, [projectData])
    React.useEffect(() => {
        if (projectFilterValues.length) {
            const isProjectNumberFilterApplied = projectFilterValues.filter(
                (item) => {
                    return item.itemField === 'number'
                }
            )
            if (
                isProjectNumberFilterApplied.length &&
                isProjectNumberFilterApplied[0].filterValues?.length
            ) {
                setIsProjectNumberVisible(true)
                return
            }
        }
        setIsProjectNumberVisible(false)
    }, [projectFilterValues])

    React.useEffect(() => {
        const isDataLoading =
            (projectDataStatus !== NetworkStatus.ready &&
                projectDataStatus !== NetworkStatus.fetchMore) ||
            projectDataStatus !== NetworkStatus.ready
        setIsLoading(isDataLoading)
    }, [projectDataStatus])

    const handleFavoriteClick = (favorite: boolean, projectId: string) => {
        handleFavoriteChange?.(favorite, projectId)
    }

    if (isLoading && allProject.length && skipCount) {
        var myDiv = document.getElementById('projectViewContainer')
        if (myDiv) myDiv.scrollTop = myDiv.scrollHeight
    }

    const handleScroll = React.useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (projectsLoading) return
            const target = event.target as HTMLDivElement
            if (
                target.scrollHeight - Math.ceil(target.scrollTop) <=
                    target.clientHeight &&
                fetchNextData &&
                projectData?.length !== projectCount
            ) {
                fetchNextData({
                    totalRows: (skipCount ?? 0) + defaultPageSize,
                })
                if (
                    Object.keys(isGridExpanded).length === 1 &&
                    !Object.values(isGridExpanded).includes(true)
                ) {
                    target.scrollTop = 0
                } else {
                    target.scrollTop = target.scrollTop - 30
                }
            }
        },
        [fetchNextData, skipCount]
    )
    React.useEffect(() => {
        if (projectsLoading === false) {
            let target = document.getElementById('projectViewContainer')
            if (target) {
                target.scrollTop = target.scrollTop - 30
            }
        }
    }, [projectsLoading])
    return (
        <>
            {isFilterExpanded && (
                <ProjectGridFilter
                    setSkipCount={setSkipCount}
                    isFilterExpanded={isFilterExpanded}
                    localStorageKey={localStorageKey}
                    localStorageItemKey={localStorageItemKey}
                    projectSortValues={projectSortValues}
                />
            )}
            {isLoading ? (
                <RecentProjectsLoadState
                    projectFilterValues={projectFilterValues}
                />
            ) : !isLoading &&
              !allProject.length &&
              projectFilterValues.length ? (
                <div
                    className={classes.centerContainer}
                    data-testid="noProjectContainer"
                >
                    <Typography className={classes.grayText}>
                        No Results Found. Try Again.
                    </Typography>
                </div>
            ) : (
                <>
                    <div
                        className={clsx(
                            classes.root,
                            classes.projectGridViewWrapper
                        )}
                        id="projectViewContainer"
                        onScroll={handleScroll}
                    >
                        <div className={classes.gridViewInner}>
                            {allProject.map((projectGrp, index) => {
                                const isExpanded = Boolean(
                                    (projectGrp.groupName &&
                                        (isGridExpanded[
                                            `groupName:${projectGrp.groupName}`
                                        ] === undefined ||
                                            isGridExpanded[
                                                `groupName:${projectGrp.groupName}`
                                            ])) ??
                                        false
                                )
                                const projectsCount =
                                    projectGrp && projectGrp.projectGroupCount
                                return (
                                    <Accordion
                                        key={`${projectGrp.groupName}-${index}`}
                                        className={classes.accordion}
                                        onChange={handleExpandCollapse(
                                            projectGrp.groupName
                                        )}
                                        expanded={isExpanded}
                                    >
                                        <AccordionSummary
                                            className={classes.accordionSumm}
                                        >
                                            {isExpanded ? (
                                                <ExpandMoreIcon
                                                    className={
                                                        classes.expansionIcon
                                                    }
                                                />
                                            ) : (
                                                <ChevronRightIcon
                                                    className={
                                                        classes.expansionIcon
                                                    }
                                                />
                                            )}
                                            <Typography
                                                className={classes.heading}
                                            >
                                                {`${projectGrp.groupName} (${projectsCount})`}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            className={classes.accordionDetail}
                                        >
                                            <div className={classes.grid}>
                                                {projectGrp &&
                                                    (
                                                        projectGrp?.projects ||
                                                        []
                                                    ).map(
                                                        (
                                                            projectItem,
                                                            index
                                                        ) => {
                                                            return (
                                                                <div
                                                                    key={`${projectItem.id}-${index}`}
                                                                >
                                                                    <ProjectGridItem
                                                                        isAdmin={
                                                                            isAdmin
                                                                        }
                                                                        activeFirmInfo={
                                                                            activeFirmInfo
                                                                        }
                                                                        project={
                                                                            projectItem
                                                                        }
                                                                        handleFavoriteChange={
                                                                            handleFavoriteClick
                                                                        }
                                                                        isProjectNumberVisible={
                                                                            isProjectNumberVisible
                                                                        }
                                                                        openRemoveDialog={
                                                                            openRemoveProjectConfirmation
                                                                        }
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                    )}
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                )
                            })}
                            {projectsLoading ? (
                                <div
                                    key={`loaderItem-0`}
                                    className={clsx(
                                        classes.itemContainer,
                                        'projectGridItemLoading'
                                    )}
                                >
                                    <Skeleton
                                        variant="rect"
                                        width="100%"
                                        height="130px"
                                    />
                                    <Skeleton
                                        variant="rect"
                                        width="100%"
                                        height="30px"
                                    />

                                    <Skeleton
                                        data-testid="numberSkeleton"
                                        variant="rect"
                                        width="100%"
                                        height="30px"
                                    />
                                </div>
                            ) : null}
                            {isLoading ? (
                                <RecentProjectsLoadState
                                    projectFilterValues={projectFilterValues}
                                />
                            ) : null}
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

const getProjectStructuredData = (
    projectData: Partial<ProjectItem>[],
    projectGroupCountItems?: Partial<ProjectGroupCountItem>[]
) => {
    const finalData: Partial<GroupProjectItem>[] = []
    if (projectData.length && projectGroupCountItems) {
        projectData.forEach((proItem: Partial<ProjectItem>) => {
            const currentProjIndex = finalData.findIndex(
                (item: Partial<ProjectItem>) =>
                    item.groupName === proItem.groupName
            )
            if (currentProjIndex > -1) {
                finalData[currentProjIndex].projects?.push(proItem)
            } else {
                finalData.push({
                    groupName: proItem.groupName,
                    number: proItem.number,
                    id: proItem.id,
                    projects: [proItem],
                    projectGroupCount: projectGroupCountItems.filter(
                        (item) => item.groupId === proItem.groupId
                    )[0]?.totalCount,
                })
            }
        })
    }
    return finalData
}

export default ProjectGridView
